import {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import MyAssessmentCard from "./MyAssessmentCard";
import { AssessmentContext } from "../contexts/assessments";
import { UserContext } from "../contexts/user";
import {
  checkIsInAssessment,
  getisCompletedAssessment,
  getAllAssessment,
} from "../axios/axiosFunctions";

// Toggle to true to show the "You might be interested in..." section
const SHOW_SUGGESTED_ASSESSMENTS = false;

const Assessments = () => {
  const { allAssessments, isLoading } = useContext(AssessmentContext);
  const { currentUser } = useContext(UserContext);
  const [assessmentsWithStatus, setAssessmentsWithStatus] = useState([]);
  const [allAvailableAssessments, setAllAvailableAssessments] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const hasFetched = useRef(false);

  const userId = currentUser?._id;

  // Fetch all available assessments once
  useEffect(() => {
    const fetchAllAssessments = async () => {
      try {
        const data = await getAllAssessment();
        setAllAvailableAssessments(data || []);
      } catch (error) {
        console.log("Error fetching all assessments:", error);
        setAllAvailableAssessments([]);
      }
    };
    fetchAllAssessments();
  }, []);

  // Fetch status for user's enrolled assessments
  useEffect(() => {
    if (isLoading || hasFetched.current || !allAssessments?.length || !userId) {
      if (!allAssessments?.length || !userId) {
        setStatusLoading(false);
      }
      return;
    }

    hasFetched.current = true;

    const fetchAllStatus = async () => {
      try {
        const statusPromises = allAssessments.map((assessment) =>
          Promise.all([
            checkIsInAssessment(assessment._id, userId),
            getisCompletedAssessment(assessment._id, userId),
          ]).then(([enrollmentRes, completionRes]) => {
            let status = "purchase"; // Default status

            if (enrollmentRes.isInAssessment) {
              if (completionRes.valid) {
                status = "view_results";
              } else {
                status = "access";
              }
            }

            return {
              ...assessment,
              inAssessment: enrollmentRes.isInAssessment,
              completed: completionRes.valid,
              reportGenerated360: completionRes.completed360,
              assessmentStatus: status, // ADD: "purchase", "access", or "view_results"
            };
          }),
        );

        const results = await Promise.all(statusPromises);
        setAssessmentsWithStatus(results);
      } catch (error) {
        console.log("Error fetching assessment status:", error);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchAllStatus();
  }, [isLoading, userId, allAssessments]);

  // Memoize filtered assessments to avoid recalculating on every render
  const myAssessments = useMemo(
    () =>
      assessmentsWithStatus.filter(
        (a) => a.inAssessment && a.assessmentStatus !== "purchase",
      ),
    [assessmentsWithStatus],
  );

  const notPurchasedAssessments = useMemo(() => {
    const enrolledIds = new Set(
      assessmentsWithStatus
        .filter((a) => a.inAssessment && a.assessmentStatus !== "purchase")
        .map((a) => a._id),
    );
    return allAvailableAssessments
      .filter((a) => !enrolledIds.has(a._id))
      .map((a) => ({
        ...a,
        assessmentStatus: "purchase",
        inAssessment: false,
        completed: false,
      }));
  }, [allAvailableAssessments, assessmentsWithStatus]);

  const loading = isLoading || statusLoading;

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="start" sx={{ padding: "48px" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {myAssessments.length > 0 && (
            <Box sx={{ backgroundColor: "#e8f4f8", padding: { xs: "24px 16px 32px", sm: "40px 48px 64px" } }}>
              <Typography variant="h4" color="secondary" fontWeight={600} mb={3}>
                My Assessments
              </Typography>
              <Grid container spacing={3}>
                {myAssessments.map((assessment) => (
                  <Grid key={assessment._id} size={12}>
                    <MyAssessmentCard assessment={assessment} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {SHOW_SUGGESTED_ASSESSMENTS && notPurchasedAssessments.length > 0 && (
            <Box sx={{ backgroundColor: "#fff", padding: { xs: "24px 16px 32px", sm: "40px 48px 64px" } }}>
              <Typography variant="h4" color="secondary" fontWeight={400} mb={3}>
                You might be interested in...
              </Typography>
              <Grid container spacing={3}>
                {notPurchasedAssessments.map((assessment) => (
                  <Grid key={assessment._id} size={12}>
                    <MyAssessmentCard assessment={assessment} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {assessmentsWithStatus.length === 0 && (
            <Box sx={{ padding: "48px" }}>
              <Typography variant="body1" color="text.secondary">
                No assessments available.
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Assessments;
